import * as React from "react";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import LoadingButton from "@mui/lab/LoadingButton";
import { useLocation, useNavigate } from "react-router-dom";
import { useGlobalAuthContext } from "../context/AuthContext";
import { useDispatch } from "react-redux";
import { login, setUserProfile } from "../Slices/roomNateSlice";
import { jwtDecode } from "jwt-decode";
import { useDologinMutation } from "../Slices/roomNateApis";
import { DynamicAlert } from "../utils/Alerts";
import { useState } from "react";
import { ReactComponent as LogoIcon } from "../Assets/mf-logo.svg";
import Cookies from "js-cookie";
import { useGlobalContext } from "../context/GlobalContext";
import posthog from 'posthog-js'
import axios from "axios";
import { baseUrl } from "../utils/baseUrl";
import { getSafeUserPostAuthDestination } from "../utils/postAuthRedirect";
import {
  getGuestLayoutUuids,
  clearGuestLayouts,
} from "../utils/guestLayoutStorage";
import { getGuestSessionId } from "../utils/guestSession";
import AuthLegalStrip from "../Components/Auth/AuthLegalStrip";
import AuthSwitchLink from "../Components/Auth/AuthSwitchLink";
import AuthPasswordField from "../Components/Auth/AuthPasswordField";
import AuthPageLayout from "../Components/Auth/AuthPageLayout";
import {
  getAuthGlassFieldSx,
  getAuthPrimaryButtonSx,
} from "../Components/Auth/authGlassStyles";

export default function SignIn() {
  const theme = useTheme();
  const [doLogin, doLoginResults] = useDologinMutation();
  const [rememberMe, setRememberMe] = useState(true);

  const { setValue } = useGlobalContext();
  const { currentUserSessionStorage, dispatch } = useGlobalAuthContext();

  const navigate = useNavigate();
  const [load, setLoad] = React.useState(false);
  const [showPsw, setShowPsw] = React.useState(false);
  const Dispatch = useDispatch();

  // =====================================================
  // DIAGNOSTIC: Log sessionStorage on component mount
  // =====================================================
  React.useEffect(() => {
    console.log('');
    console.log('╔' + '═'.repeat(60) + '╗');
    console.log('║  📍 [LOGIN PAGE] Component Mounted                          ║');
    console.log('╠' + '═'.repeat(60) + '╣');
    console.log('║  Current sessionStorage values:');
    console.log('║    redirectPath:', sessionStorage.getItem("redirectPath") || '(not set)');
    console.log('║    needsCopy:', sessionStorage.getItem("needsCopy") || '(not set)');
    console.log('║    layoutIdToCopy:', sessionStorage.getItem("layoutIdToCopy") || '(not set)');
    console.log('║  ');
    console.log('║  currentUserSessionStorage:', !!currentUserSessionStorage);
    console.log('╚' + '═'.repeat(60) + '╝');
    console.log('');
  }, [currentUserSessionStorage]);

  const [title, setTitle] = useState("");
  const [status, setStatus] = useState(false);

  // Surfaces that bounce a user here rather than failing in place (e.g. signup
  // with an email that already has an account) hand over the address they
  // typed plus the copy to show, so nothing has to be re-entered or guessed.
  const location = useLocation();
  const handoff = location.state || {};
  const [notice, setNotice] = useState(handoff.notice || "");

  // Capture guest uuids at submit time so the useEffect-based success handler
  // (which can fire later, after storage pruning) can still clear them.
  const sentGuestUuidsRef = React.useRef([]);

  const formik = useFormik({
    initialValues: {
      email: handoff.email || "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Enter your email")
        .matches(
          "^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$",
          "Enter a valid email address"
        ),
      password: Yup.string()
        .required("Choose a password")
        .min(8, "Password must be at least 8 characters."),
    }),
    onSubmit: async (values) => {
      setLoad(true);
      setNotice("");
      try {
        const guestUuids = getGuestLayoutUuids();
        sentGuestUuidsRef.current = guestUuids;
        const body = { ...values };
        if (guestUuids.length > 0) body.guestLayoutUuids = guestUuids;
        // Claim any layouts uploaded under this browser's guest session — this
        // covers uploads whose completion the client never saw (user left the
        // page before processing finished), which the uuid list alone misses.
        // Send both casings: the session claim reads snake_case, while the
        // backend's guest-session lookups elsewhere key on camelCase.
        const guestSessionId = getGuestSessionId();
        if (guestSessionId) {
          body.guest_session_id = guestSessionId;
          body.guestSessionId = guestSessionId;
        }
        doLogin({ body });
      } catch (error) {
        console.error('Login failed:', error);
      }
    },
  });

  

  React.useEffect(() => {
  const handleLoginSuccess = async () => {
    const user = doLoginResults?.data?.user;
    const authToken = doLoginResults?.data?.token;
    const role = user?.role;

    if (user?.status === "blocked") {
      setLoad(false);
      setTitle("User blocked");
      setStatus(false);
      setTimeout(() => {
        setTitle("");
      }, 2000);
      return;
    }

    if (user?.origin && user?.origin !== window.location.origin && !window.location.hostname.includes('localhost')) {
      window.location.href = `${user.origin}/partner-login?token=${authToken}&rememberMe=${rememberMe}`;
      return;
    }

    if (window.opener) {
      try {
        const openerPath = new URL(window.opener.location.href).pathname;
        if (openerPath === "/main") {
          window.opener.postMessage(
            {
              authToken,
              role,
              status: "success",
            },
            window.location.origin
          );
          window.close();
          return;
        }
      } catch (err) {
        console.warn("Access denied to opener location:", err);
      }
    }

    // Local login state update
    dispatch({ type: "LOGIN", payload: authToken });
    Dispatch(login(authToken)); // Make sure you only need one of these (dispatch/Dispatch)
    
    // Store user profile data immediately from login response
    if (user) {
      Dispatch(setUserProfile(user));
    }

    Cookies.set("token", JSON.stringify(authToken), {
      expires: rememberMe ? 30 : 7,
    });

    if (sentGuestUuidsRef.current && sentGuestUuidsRef.current.length > 0) {
      clearGuestLayouts(sentGuestUuidsRef.current);
      sentGuestUuidsRef.current = [];
    }

    setLoad(false);

    posthog.identify(user._id, {
      ...user
    });

    // =====================================================
    // PR #93: LOGIN SUCCESS - COPY FIRST, THEN NAVIGATE ONCE
    // =====================================================
    // CRITICAL RULE: If needsCopy=true, user must NEVER enter
    // the original UUID editor (even for 0.1 seconds).
    //
    // Flow:
    // 1. Read sessionStorage flags
    // 2. Clear sessionStorage flags immediately
    // 3. If needsCopy=false: navigate to redirectPath (once)
    // 4. If needsCopy=true: copy FIRST (blocking), then navigate to NEW UUID (once)
    //
    // Binary pass/fail:
    // - PASS: first editor URL is NEW_UUID (when needsCopy=true)
    // - FAIL: first editor URL is ORIGINAL_UUID (overwrite risk)
    // =====================================================
    const redirectPath = sessionStorage.getItem("redirectPath");
    const needsCopy = sessionStorage.getItem("needsCopy") === "true";
    const layoutIdToCopy = sessionStorage.getItem("layoutIdToCopy");
    
    console.log('');
    console.log('╔' + '═'.repeat(70) + '╗');
    console.log('║  🔐 [LOGIN SUCCESS] Handler executing                                ║');
    console.log('╠' + '═'.repeat(70) + '╣');
    console.log('║  RESPONSE DATA:');
    console.log('║    role:', role);
    console.log('║    userId:', user?._id);
    console.log('║    token exists:', !!authToken);
    console.log('║  ');
    console.log('║  SESSION STORAGE VALUES (BEFORE clearing):');
    console.log('║    redirectPath:', redirectPath || '(not set)');
    console.log('║    needsCopy (raw):', sessionStorage.getItem("needsCopy"));
    console.log('║    needsCopy (parsed):', needsCopy);
    console.log('║    layoutIdToCopy:', layoutIdToCopy || '(not set)');
    console.log('║  ');
    console.log('║  DECISION LOGIC:');
    console.log('║    Will skip copy (needsCopy=false)?', !needsCopy);
    console.log('║    Will attempt copy (needsCopy=true)?', needsCopy);
    console.log('║    Has layoutIdToCopy?', !!layoutIdToCopy);
    console.log('╚' + '═'.repeat(70) + '╝');
    console.log('');

    // Clear sessionStorage flags immediately (prevents loops/races)
    sessionStorage.removeItem("redirectPath");
    sessionStorage.removeItem("needsCopy");
    sessionStorage.removeItem("layoutIdToCopy");
    console.log('[LOGIN] Cleared sessionStorage flags');

    if (role === "user") {
      setValue("1");
      
      // =====================================================
      // CASE 1: needsCopy=false - navigate directly (once)
      // =====================================================
      // FIX: Use window.location.href instead of navigate()
      // 
      // ROOT CAUSE: navigate() is async. After auth state changes (dispatch, 
      // Cookies.set), React re-renders BEFORE navigate completes. This causes
      // the route to reset to "/" which then redirects to "/main".
      //
      // SOLUTION: window.location.href performs a hard redirect that bypasses
      // React's reconciliation entirely, guaranteeing user lands on the correct
      // destination.
      // =====================================================
      if (!needsCopy) {
        const destination = getSafeUserPostAuthDestination(redirectPath);
        console.log('');
        console.log('╔' + '═'.repeat(70) + '╗');
        console.log('║  🚀 [LOGIN] CASE 1: needsCopy=false - DIRECT NAVIGATION           ║');
        console.log('╠' + '═'.repeat(70) + '╣');
        console.log('║  redirectPath:', redirectPath || '(not set)');
        console.log('║  Final destination:', destination);
        console.log('║  ');
        console.log('║  ⚡ Using window.location.href (hard redirect)');
        console.log('║  (bypasses React re-render race condition)');
        console.log('╚' + '═'.repeat(70) + '╝');
        console.log('');
        window.location.href = destination;
        return;
      }
      
      // =====================================================
      // CASE 2: needsCopy=true - COPY FIRST, then navigate (once)
      // =====================================================
      // CRITICAL: Do NOT navigate to original UUID.
      // User must never mount editor on someone else's layout.
      console.log('');
      console.log('╔' + '═'.repeat(70) + '╗');
      console.log('║  🔄 [LOGIN] CASE 2: needsCopy=true - COPY FIRST                   ║');
      console.log('╠' + '═'.repeat(70) + '╣');
      console.log('║  layoutIdToCopy:', layoutIdToCopy || '(not set)');
      console.log('║  redirectPath:', redirectPath || '(not set)');
      console.log('╚' + '═'.repeat(70) + '╝');
      console.log('');
      
      if (layoutIdToCopy) {
        try {
          const pathCopy = `/savedLayout/copy/${layoutIdToCopy}`;
          console.log('[LOGIN] ⏳ Copying layout...');
          console.log('[LOGIN]   API endpoint:', `${baseUrl}${pathCopy}`);
          
          const response = await axios.post(
            `${baseUrl}${pathCopy}`,
            { parent: null },
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
          const newUuid = response?.data?.data?.uuid;
          console.log('[LOGIN] ✅ Layout copied successfully!');
          console.log('[LOGIN]   New UUID:', newUuid);

          // Navigate to the NEW UUID (never the original)
          if (redirectPath && redirectPath.includes("/floor-plan/") && newUuid) {
            const versionMatch = redirectPath.match(/version\/([^/]+)/);
            const version = versionMatch ? versionMatch[1] : "1";
            const newRedirectPath = `/floor-plan/${newUuid}/version/${version}`;
            console.log('');
            console.log('╔' + '═'.repeat(70) + '╗');
            console.log('║  🚀 [LOGIN] NAVIGATING TO COPIED LAYOUT                          ║');
            console.log('╠' + '═'.repeat(70) + '╣');
            console.log('║  Original redirectPath:', redirectPath);
            console.log('║  Extracted version:', version);
            console.log('║  New UUID:', newUuid);
            console.log('║  Final destination:', newRedirectPath);
            console.log('║  ');
            console.log('║  ⚡ Using window.location.href (hard redirect)');
            console.log('╚' + '═'.repeat(70) + '╝');
            console.log('');
            window.location.href = newRedirectPath;
            return;
          } else {
            console.log('[LOGIN] ⚠️ Condition failed:');
            console.log('[LOGIN]   redirectPath:', redirectPath);
            console.log('[LOGIN]   redirectPath includes /floor-plan/:', redirectPath?.includes("/floor-plan/"));
            console.log('[LOGIN]   newUuid exists:', !!newUuid);
            console.log('[LOGIN]   Falling through to /main...');
          }
        } catch (error) {
          console.error("[LOGIN] ❌ Error copying layout:", error);
          console.error("[LOGIN]   Error message:", error?.message);
          console.error("[LOGIN]   Error response:", error?.response?.data);
          // Copy failed - go to dashboard, don't risk overwriting original
          console.log('[LOGIN] Copy failed, using window.location.href to /main for safety');
          window.location.href = "/main";
          return;
        }
      } else {
        console.log('[LOGIN] ⚠️ No layoutIdToCopy provided!');
      }
      
      // Fallback: no layoutIdToCopy or no redirectPath
      console.log('');
      console.log('╔' + '═'.repeat(70) + '╗');
      console.log('║  ⚠️ [LOGIN] FALLBACK - No valid copy/redirect                     ║');
      console.log('╠' + '═'.repeat(70) + '╣');
      console.log('║  layoutIdToCopy:', layoutIdToCopy || '(not set)');
      console.log('║  redirectPath:', redirectPath || '(not set)');
      console.log('║  ');
      console.log('║  ⚡ Using window.location.href = "/main"');
      console.log('╚' + '═'.repeat(70) + '╝');
      console.log('');
      window.location.href = "/main";
    } else {
      const destination =
        redirectPath?.startsWith("/analytics") ? redirectPath : "/";
      console.log(
        "[LOGIN] Non-user role, using window.location.href to",
        destination
      );
      window.location.href = destination;
    }
  };

  if (doLoginResults.isSuccess) {
    handleLoginSuccess();
  }

  if (doLoginResults.isError) {
    setLoad(false);
    setTitle(doLoginResults?.error?.data || doLoginResults?.error?.error);
    setStatus(false);
    setTimeout(() => {
      setTitle("");
    }, 2000);
  }
}, [doLoginResults, Dispatch, dispatch, navigate, rememberMe, setValue]);

  // Track if user was already logged in when component mounted
  const wasLoggedInOnMount = React.useRef(!!currentUserSessionStorage);
  
  React.useEffect(() => {
    // Only redirect if user was ALREADY logged in when page loaded
    // Don't redirect if they just logged in (the login handler handles that)
    if (currentUserSessionStorage && wasLoggedInOnMount.current) {
      const decoded = jwtDecode(currentUserSessionStorage);
      decoded?.role === "user" ? navigate("/dashboard") : navigate("/");
    }
  }, [currentUserSessionStorage, navigate]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      {title?.length >= 1 && <DynamicAlert status={status} title={title} />}
      {!title && notice && <DynamicAlert severity="info" title={notice} />}

      <AuthPageLayout
        footer={
          <>
            <Box sx={{ mt: 2.5, textAlign: "center" }}>
              <AuthSwitchLink
                mutedText="New to MagicFurnish?"
                linkText="Create a free account"
                to="/signup"
                sx={{ mt: 0 }}
              />
            </Box>
            <AuthLegalStrip sx={{ pt: 1.5, pb: 0.5 }} />
          </>
        }
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
          <LogoIcon width={52} height={52} />
        </Box>

        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "21px", sm: "22px" },
            fontWeight: 600,
            color: "text.primary",
            letterSpacing: "-0.02em",
            mb: 2.5,
            textAlign: "center",
            lineHeight: 1.25,
            px: 0.5,
          }}
        >
          Welcome back!
        </Typography>

        <Box component="form" onSubmit={formik.handleSubmit} noValidate>
          <Grid container spacing={1.75}>

            <Grid item xs={12}>
              <TextField
                variant="outlined"
                label={
                  formik.touched.email && formik.errors.email
                    ? formik.errors.email
                    : "Email"
                }
                onBlur={formik.handleBlur}
                required
                fullWidth
                id="email"
                InputProps={{ style: { height: 48 } }}
                inputProps={{
                  autoComplete: "email",
                  inputMode: "email",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                  enterKeyHint: "next",
                }}
                value={formik?.values.email}
                name="email"
                type="email"
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                className={
                  formik.touched.email && formik.errors.email ? null : "white"
                }
                sx={getAuthGlassFieldSx(theme)}
              />
            </Grid>

            <Grid item xs={12}>
              <AuthPasswordField
                variant="outlined"
                label={
                  formik.touched.password && formik.errors.password
                    ? formik.errors.password
                    : "Password"
                }
                required
                fullWidth
                name="password"
                id="password"
                value={formik?.values.password}
                showPsw={showPsw}
                onToggle={() => setShowPsw((v) => !v)}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                className={
                  formik.touched.password && formik.errors.password ? null : "white"
                }
                inputProps={{
                  autoComplete: "current-password",
                  enterKeyHint: "go",
                  autoCorrect: "off",
                  autoCapitalize: "none",
                  spellCheck: false,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ minHeight: 48 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      value="remember"
                      size="small"
                      sx={{ p: "6px" }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "13px", fontWeight: 400, color: "rgba(0,0,0,0.50)" }}>
                      Remember me
                    </Typography>
                  }
                  sx={{ mr: 0, ml: "6px", alignItems: "center" }}
                />
                <Box
                  component={Link}
                  to="/forgetPsw"
                  sx={{
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 44,
                    minWidth: 44,
                    px: 1,
                    fontSize: "13px",
                    fontWeight: 400,
                    color: "var(--brand-color, #006766)",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Forgot password?
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <LoadingButton
                loading={load}
                variant="contained"
                fullWidth
                type="submit"
                disableElevation
                sx={{
                  ...getAuthPrimaryButtonSx(theme),
                  mt: 0.5,
                }}
              >
                Log in
              </LoadingButton>
            </Grid>

          </Grid>
        </Box>
      </AuthPageLayout>
    </>
  );
}
